// Terms.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { Card } from '../components/UI/card';
import { Button } from '../components/UI/button';
import logo from '../assets/FLUXLAB-Logo.png';
import { Phone, Mail } from 'lucide-react';
import { Link } from "react-router-dom";

const Terms = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white text-gray-800">
      {/* Navigation - simplified, assuming shared or no motion for now */}
      <motion.nav
  className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm"
  
  initial={{ y: -100 }}
  animate={{ y: 0 }}
  transition={{ duration: 0.6 }}
>
  <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
    {/* 🟢 Logo Section */}
    <div
      className="flex items-center gap-3 cursor-pointer"
      onClick={scrollToTop}
    >
      <img
        src={logo}
        alt="Company Logo"
        className="w-[110px] h-auto" // Increased by 30%
      />
    </div>

    {/* 🔵 Navigation Links */}
    <div className="hidden md:flex items-center gap-8">
         <a
        href="/"
        className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
      >
        Home
      </a>
      <a
        href="#about"
        className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
      >
        About
      </a>
      <a
        href="#services"
        className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
      >
        Services
      </a>
      <a
        href="#why-us"
        className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
      >
        Why Us
      </a>
      <a
        href="#contact"
        className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
      >
        Contact
      </a>
    </div>

    {/* 🔴 Call-to-Action Button */}
    <a href="#contact">
      <Button className="bg-[#2563eb] hover:bg-red-700 text-white flex items-center">
        <Phone className="w-4 h-4 mr-2" />
        Get Quote
      </Button>
    </a>
  </div>
</motion.nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-bold mb-6 mt-12"
          >
            Terms of Service
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl italic tracking-wide font-light"
          >
            Important legal information
          </motion.p>
        </div>
      </section>

      {/* Terms Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="prose prose-lg max-w-none">
          <p>
            FLUXLAB (“us”, “we” or “our” as the context requires)
            provides an informational website and platform for accessing content,
            articles, guides, and other general information (the “Services”) via our
            websites, and other platforms (the “Platforms”). For the purpose of this
            Terms of Service, wherever the context so requires "you" or "your" shall
            mean The User.
          </p>

          {/* ------------------ POINT 1 WITH LINKS ------------------ */}
          <h4 style={{ color: "#0a4d68", marginTop: "30px" }}>1. ACCEPTANCE OF TERMS</h4>
          <p>
            These terms and conditions (“Terms”) govern your access to and use of our
            Services and Platforms, together with our{" "}
            <Link
              to="/privacy"
              style={{ color: "#0a4d68", textDecoration: "underline" }}
            >
              privacy policy
            </Link>{" "}
            and{" "}
            <Link
              to="/Cookie"
              style={{ color: "#0a4d68", textDecoration: "underline" }}
            >
              cookie policy
            </Link>
            . By accessing or using the Services or Platforms, you are confirming that
            you are above 18 Years of age, have read, understood and agreed to these
            Terms and those of the {" "}
            <Link
              to="/privacy"
              style={{ color: "#0a4d68", textDecoration: "underline" }}
            >
              privacy policy
            </Link>{" "}
            and{" "}
            <Link
              to="/Cookie"
              style={{ color: "#0a4d68", textDecoration: "underline" }}
            >
              cookie policy
            </Link>
          </p>
          <p>
            We may amend these Terms at any time at our discretion. If these Terms are
            amended, we will publish the revised terms and conditions and you will be
            deemed to have accepted any amendments if you continue using our Services or
            Platforms after the amendments are displayed. If you do not accept all of
            these Terms then you should not use our Services or Platforms.
          </p>

          {/* ------------------ POINT 2 ------------------ */}
          <h4 style={{ color: "#0a4d68", marginTop: "30px" }}>2. ELIGIBILITY TO USE</h4>
          <p>
            You must be at least 18 years of age and must possess the legal authority to
            enter into an agreement so as to become a User and use the services of
            FLUXLAB. If you are a minor or are below the age of 18
            years, you shall not register as a User of the Website and shall not
            transact on, via or use the Website.
          </p>

          {/* ------------------ POINT 3 ------------------ */}
          <h4 style={{ color: "#0a4d68", marginTop: "30px" }}>3. USER OBLIGATIONS AND PROHIBITED ACTIVITIES</h4>
          <p>
            You agree not to use the Services or Platforms for any illegal,
            unauthorized, unintended, or harmful purpose. Prohibited activities
            include, but are not limited to:
          </p>
          <ul style={{ listStyle: "disc", marginLeft: "40px" }}>
            <li>Violating any applicable local, state, national, or international law or regulation.</li>
            <li>Infringing upon or violating our intellectual property rights or the intellectual property rights of others.</li>
            <li>Engaging in spamming, harassment, or transmitting viruses, worms, or any code of a destructive nature.</li>
            <li>
              Attempting to gain unauthorized access to any portion or feature of the
              Platforms, or any other systems or networks connected to the Platforms.
            </li>
          </ul>

          {/* ------------------ POINT 4 ------------------ */}
          <h4 style={{ color: "#0a4d68", marginTop: "30px" }}>4. INTELLECTUAL PROPERTY RIGHTS</h4>
          <p>
            The Platforms and all content, materials, information, and intellectual
            property on the Platforms, including but not limited to text, graphics,
            logos, images, software, and the compilation thereof (collectively, the
            “Content”), are the exclusive property of FLUXLAB and are
            protected by copyright, trademark, and other intellectual property laws
          </p>
          <p>
            You agree not to modify, copy, distribute, transmit, display, perform,
            reproduce, publish, license, create derivative works from, transfer, or sell
            any information, software, products, or services obtained from the Platforms,
            except for your own personal, non-commercial use.
          </p>

          {/* ------------------ POINT 5 ------------------ */}
          <h4 style={{ color: "#0a4d68", marginTop: "30px" }}>5. DISCLAIMER OF INFORMATION AND NO PROFESSIONAL ADVICE</h4>
          <p><strong>A. Informational Purposes Only:</strong> The content provided on our Platforms and Services is for general informational purposes only and is not intended to serve as professional advice.</p>
          <p><strong>B. No Reliance:</strong> You acknowledge that any reliance upon the Services and Platforms, including any Content, shall be at your sole risk. FLUXLAB is not responsible or liable for any loss or damage of any kind arising from the use of, or reliance on, any Content.</p>

          {/* ------------------ POINT 6 ------------------ */}
          <h4 style={{ color: "#0a4d68", marginTop: "30px" }}>6. DISCLAIMER OF WARRANTIES</h4>
          <p>
            The Services and Platforms are provided on an “as is” and “as available”
            basis FLUXLAB hereby expressly disclaims all warranties of
            any kind, whether express or implied, including, but not limited to, implied
            warranties of merchantability, fitness for a particular purpose, and
            non-infringement. We make no warranty that the Services or Platforms will
            meet your requirements, or that your use of the Services or Platforms will be
            uninterrupted, timely, secure, or error-free.
          </p>

          {/* ------------------ POINT 7 ------------------ */}
          <h4 style={{ color: "#0a4d68", marginTop: "30px" }}>7. LIMITATION OF LIABILITY</h4>
          <p>
            In no event shall FLUXLAB be liable for any direct,
            indirect, punitive, incidental, special or consequential damages, and any
            other damages like damages for loss of use, data or profits, arising out of
            or in any way connected with the use or performance of the Website or any
            other Sales Channel.
          </p>

          {/* ------------------ POINT 8 ------------------ */}
          <h4 style={{ color: "#0a4d68", marginTop: "30px" }}>8. LINKS TO THIRD-PARTY SITES</h4>
          <p>
            The Platforms may contain links to other websites ("Linked Sites"). The
            Linked Sites are not under the control of FLUXLAB and we are
            not responsible for the content of any Linked Site. The inclusion of any link
            does not imply endorsement by FLUXLAB of the site or any
            association with its operators. You use the Linked Sites at your own risk.
          </p>

          {/* ------------------ POINT 9 ------------------ */}
          <h4 style={{ color: "#0a4d68", marginTop: "30px" }}>9. GOVERNING LAW AND JURISDICTION</h4>
          <p>
            This Agreement shall be governed by the laws of Republic of India. You hereby
            consent to the exclusive jurisdiction and venue of courts in New Delhi, India
            in all disputes arising out of or relating to the use of the
            Platforms/Services.
          </p>

          {/* ------------------ POINT 10 ------------------ */}
          <h4 style={{ color: "#0a4d68", marginTop: "30px" }}>10. TERMINATION</h4>
          <p>
            We reserve the right, in our sole discretion, to terminate your access to the
            Platforms and the related Services or any portion thereof at any time, without
            notice.
          </p>

          {/* ------------------ POINT 11 ------------------ */}
          <h4 style={{ color: "#0a4d68", marginTop: "30px" }}>11. CONTACT US</h4>
          <p>
            If you require further information about FLUXLAB or have any
            suggestions concerning how to improve our Services or Platforms, please
            contact our helpdesk/Contact Us. Alternatively, you can write to us at
            sales@fluxlab.in.
          </p>
</div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Company Info */}
            <div className="text-left">
              <div className="flex items-center gap-3 mb-4 cursor-pointer" onClick={scrollToTop}>
                <img
                  src={logo}
                  alt="FLUXLAB Logo"
                  className="w-[110px] h-auto"
                />
              </div>
              <p className="text-sm leading-relaxed">
                Leading manufacturer of automotive plastic components with a
                decade of excellence.
              </p>
            </div>

            {/* Quick Links */}
            <div className="text-left">
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="/#about"
                    className="hover:text-white transition-colors inline-block"
                  >
                    About Us
                  </a>
                </li>
                <li>
                  <a
                    href="/#services"
                    className="hover:text-white transition-colors inline-block"
                  >
                    Services
                  </a>
                </li>
                <li>
                  <a
                    href="/#why-us"
                    className="hover:text-white transition-colors inline-block"
                  >
                    Why Choose Us
                  </a>
                </li>
                <li>
                  <a
                    href="/#contact"
                    className="hover:text-white transition-colors inline-block"
                  >
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal Links */}
            <div className="text-left">
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="/terms"
                    className="hover:text-white transition-colors inline-block"
                  >
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a
                    href="/privacy"
                    className="hover:text-white transition-colors inline-block"
                  >
                    Privacy Policy
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="text-left">
              <h4 className="text-white font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-2">
                  <Phone className="w-4 h-4 mt-1 flex-shrink-0 text-blue-500" />
                  <span>+91 9099159922</span>
                </li>
                <li className="flex items-start gap-2">
                  <Mail className="w-4 h-4 mt-1 flex-shrink-0 text-blue-500" />
                  <span>sales@fluxlab.in</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="border-t border-slate-800 pt-8 text-center text-sm">
            <p>
              &copy; {new Date().getFullYear()} FLUXLAB. All rights reserved. | Precision Manufacturing Excellence
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Terms;